// src/hooks/usePermissions.ts
import { useCallback, useMemo } from 'react';
import { IProfile } from '~/utils/interface/user';
import { selectProfile } from '~/thunks/user/userSlice';
import { useAppSelector } from '~/utils/redux/hooks';
import { PermissionString } from '~/utils/interface/permissions';

type PermissionsMap = Record<string, PermissionString>;

export const usePermissions = <T extends PermissionsMap>(permissions?: T) => {
  const profile: IProfile = useAppSelector(selectProfile);
  const userPermissions = profile.wildcards;

  const hasPermission = useCallback(
    (requiredPermission: PermissionString): boolean => {
      // if (permissions.direct.includes(requiredPermission)) return true;

      const [requiredEntity, requiredAction] = requiredPermission.split(':');

      return userPermissions?.some((wildcard) => {
        const [wildcardEntity, wildcardAction] = wildcard.split(':');
        const entityMatch = wildcardEntity === '*' || wildcardEntity === requiredEntity;
        const actionMatch = wildcardAction === '*' || wildcardAction === requiredAction;
        return entityMatch && actionMatch;
      });
    },
    [userPermissions]
  );

  const checkedPermissions = useMemo(() => {
    const results: Record<keyof T, boolean> = {} as any;

    if (!permissions) {
      return results;
    }

    for (const key in permissions) {
      const permissionString = permissions[key];
      results[key] = hasPermission(permissionString);
    }

    return results;
  }, [permissions, hasPermission]);

  return {
    hasPermission,
    ...checkedPermissions,
  };
};
